import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../lib/api';
import { prepareCaptureStorage, saveCapture, removeCapture } from './captureQueue';

export const captureStatus = e => {
  if (e.error) return e.error;
  if (e.scan_status === 'checking' || e.scan_status === 'queued') return `${e.received_at ? 'Received at till' : 'Uploaded'} · Checking prescription`;
  if (e.scan_status === 'needs_retry') return `${e.received_at ? 'Received at till' : 'Uploaded'} · Needs retry`;
  if (e.status === 'applied') return 'Applied';
  if (e.status === 'dismissed' || e.status === 'superseded') return e.status;
  if (e.received_at) return e.kind === 'photo' ? 'Received at till · Review required' : 'Received at till · Awaiting confirmation';
  return { uploaded: 'Uploaded · Waiting for till', sending: 'Sending', waiting: 'Waiting for connection', captured: 'Captured' }[e.status] || e.status;
};
const detail = e => typeof e.response?.data?.detail === 'string' ? e.response.data.detail : e.message;

export const usePhoneScanner = (user) => {
  const uid = user.user_id;
  const prefix = 'pos-phone:'+uid;
  const [phoneId] = useState(() => { let id = localStorage.getItem(prefix+':device'); if (!id) { id = crypto.randomUUID(); localStorage.setItem(prefix+':device', id); } return id; });
  const [sid, setSid] = useState(() => localStorage.getItem(prefix+':session') || '');
  const [session, setSession] = useState(null), [queue, setQueue] = useState([]), [safe, setSafe] = useState(false), [error, setError] = useState('');
  const live = useRef({ queue, sid }); live.current = { queue, sid };
  const busy = useRef(false), finished = useRef(false);
  const writing = useRef(0);
  const put = useCallback(async e => {
    await saveCapture(uid, e);
    setQueue(q => { const next = [...q.filter(x => x.event_id !== e.event_id), e].sort((a,b) => a.created_at.localeCompare(b.created_at)); live.current.queue = next; return next; });
  }, [uid]);
  useEffect(() => { let active = true; prepareCaptureStorage(uid).then(q => { if (active) { setQueue(q); setSafe(true); } }).catch(e => setError(detail(e))); return () => { active = false; }; }, [uid]);
  const poll = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    try {
      const target = live.current.sid;
      if (target) {
        const { data } = await api.get(`/scan-pos/sessions/${target}`);
        setSession(data);
        for (const remote of data.events || []) {
          const local = live.current.queue.find(e => e.event_id === remote.event_id);
          const durable = !!remote.upload_complete;
          const resolved = ['applied','dismissed','superseded'].includes(remote.status);
          // A "sending" server reservation is not a stored photo. Keep the local
          // retry/error state and encrypted bytes until real upload or resolution.
          if (local && (durable || resolved) && (remote.status !== local.status || remote.received_at !== local.received_at || remote.scan_status !== local.scan_status || durable !== local.uploaded)) {
            await put({ ...local, status: remote.status, received_at: remote.received_at, scan_status: remote.scan_status,
              uploaded: durable, resolved, photo: remote.received_at || resolved ? null : local.photo, error: '' });
          }
        }
      }
      for (const e of live.current.queue) {
        if (e.uploaded || e.resolved || e.session_id !== target || e.retry_at > Date.now()) continue;
        try {
          await put({ ...e, status: 'sending', error: '' });
          if (e.kind === 'photo') {
            if (!e.photo) throw new Error('Photo unavailable. Retake or dismiss this capture.');
            const form = new FormData(); form.append('file', e.photo, 'prescription.jpg'); form.append('event_id', e.event_id); form.append('phone_id', phoneId);
            if (e.replaces) form.append('replaces', e.replaces);
            await api.post(`/scan-pos/sessions/${e.session_id}/photos`, form, { timeout: 120000, headers: { 'Content-Type': 'multipart/form-data' } });
          } else await api.post(`/scan-pos/sessions/${e.session_id}/codes`, { event_id: e.event_id, code: e.code, phone_id: phoneId });
          await put({ ...e, uploaded: true, status: 'uploaded', error: '' });
        } catch (err) {
          await put({ ...e, status: 'waiting', error: err.response ? detail(err) : '', retry_at: Date.now()+10000 });
        }
      }
    } catch (e) { if (e.response) setError(detail(e)); }
    finally { busy.current = false; }
  }, [phoneId, put]);
  useEffect(() => { if (!safe) return; poll(); const timer = setInterval(poll, 2000); window.addEventListener('online', poll); return () => { clearInterval(timer); window.removeEventListener('online', poll); }; }, [poll, safe, sid]);
  const connect = async id => {
    const { data } = await api.post(`/scan-pos/sessions/${id}/connect`, { phone_id: phoneId, phone_name: 'Staff phone' });
    setSession(data); setSid(id); live.current.sid = id; localStorage.setItem(prefix+':session', id); finished.current = false;
  };
  const capture = async (kind, value, replaces = '') => {
    if (!safe || !sid || session?.state !== 'connected' || finished.current) throw new Error('An approved, open sale and protected storage are required');
    const event = { event_id: crypto.randomUUID(), session_id: sid, sale_ref: session.sale_ref, till_name: session.name,
      kind, code: kind === 'barcode' ? value : '', photo: kind === 'photo' ? value : null, replaces,
      created_at: new Date().toISOString(), status: navigator.onLine ? 'captured' : 'waiting' };
    writing.current++;
    try { await put(event); } catch (e) { setSafe(false); setError(detail(e)); throw e; } finally { writing.current--; }
    poll();
  };
  const finish = async () => {
    finished.current = true;
    try {
      if (writing.current) throw new Error('A capture is still being saved. Finish again in a moment.');
      const own = live.current.queue.filter(e => e.session_id === sid);
      if (own.some(e => !e.uploaded && !e.resolved)) throw new Error('Wait until all pending captures have uploaded');
      const { data: current } = await api.get(`/scan-pos/sessions/${sid}`);
      await api.post(`/scan-pos/sessions/${sid}/finish`, { phone_id: phoneId, event_ids: [...new Set([...own.map(e => e.event_id), ...current.events.map(e => e.event_id)])] });
      await poll();
    } catch (e) { finished.current = false; throw e; }
  };
  const end = async reason => { await api.post(`/scan-pos/sessions/${sid}/action`, { action: 'end', reason }); await poll(); };
  const discard = async e => { await removeCapture(uid, e.event_id); setQueue(q => q.filter(x => x.event_id !== e.event_id)); };
  const discardPending = async (e, reason) => {
    await api.post(`/scan-pos/sessions/${e.session_id}/discard-pending`, { phone_id: phoneId, event_id: e.event_id, reason });
    await put({ ...e, photo: null, uploaded: false, resolved: true, status: 'dismissed', error: '' });
  };
  const reassign = async e => {
    if (session?.state !== 'connected' || e.session_id === sid) throw new Error('Connect to another authorised sale first');
    const { data } = await api.post(`/scan-pos/sessions/${e.session_id}/reassign`, { target_session_id: sid, phone_id: phoneId, event_id: e.event_id });
    if (data.local_only) {
      await put({ ...e, session_id: sid, sale_ref: session.sale_ref, till_name: session.name, uploaded: false, status: 'captured', error: '', retry_at: 0 });
    } else {
      await put({ ...e, event_id: data.event_id, session_id: sid, sale_ref: session.sale_ref, till_name: session.name, uploaded: true, status: 'uploaded', received_at: null, error: '' });
      await discard(e);
    }
    await poll();
  };
  return { sid, session, queue, safe, error, connect, capture, finish, end, discard, discardPending, reassign };
};